import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Sparkles } from "lucide-react";

export function SponsoredOnboardingModal() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: ro }, { data: p }] = await Promise.all([
          api.get("/sponsored/roles"),
          api.get("/job-preferences"),
        ]);
        if (ro?.is_sponsored && (!p?.roles || p.roles.length === 0)) {
          setOpts(ro); setPrefs(p || {}); setOpen(true);
        }
      } catch (e) { /* non-sponsored or not ready — no modal */ }
    })();
  }, []);

  if (!opts) return null;
  const max = opts.max_select || 10;
  const toggle = (name) => setSelected((cur) =>
    cur.includes(name) ? cur.filter((r) => r !== name) : (cur.length >= max ? cur : [...cur, name]));

  const save = async () => {
    if (!selected.length) { toast.error("Pick at least one role from the list."); return; }
    setSaving(true);
    try {
      await api.put("/job-preferences", {
        roles: selected,
        locations: prefs?.locations || [],
        country: prefs?.country || "gb",
        remote: prefs?.remote || "any",
        employment_type: prefs?.employment_type || null,
        seniority: prefs?.seniority || null,
        min_salary: prefs?.min_salary || null,
        keywords_include: [],
        keywords_exclude: prefs?.keywords_exclude || [],
        enabled: prefs?.enabled !== false,
      });
      toast.success("Roles saved — your daily matches will start on these.");
      setOpen(false);
      navigate("/matches");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save your roles.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="sponsored-onboarding-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-left">
            <Sparkles className="h-5 w-5 text-[#1a3060] shrink-0" /> Welcome to the Sponsored tier — choose your roles
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground text-left">Pick up to {max} roles. We'll match you 12 sponsor-licensed &amp; NHS jobs a day against these. You can change them anytime.</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="onboarding-disclaimer">{opts.disclaimer}</div>
        <div className="space-y-3">
          <Label className="text-xs font-semibold uppercase tracking-wider">Selected {selected.length}/{max}</Label>
          {(opts.categories || []).map((cat) => (
            <div key={cat}>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">{cat}</p>
              <div className="flex flex-wrap gap-1.5">
                {((opts.roles || {})[cat] || []).map((r) => {
                  const on = selected.includes(r.name);
                  const disabled = !on && selected.length >= max;
                  return (
                    <button type="button" key={r.id} onClick={() => toggle(r.name)} disabled={disabled} data-testid={`onboarding-role-${r.id}`}
                      className={`text-xs rounded-full px-3 py-1 border transition-colors ${on ? "bg-[#1a3060] text-white border-[#1a3060]" : disabled ? "bg-zinc-50 text-zinc-300 border-zinc-200 cursor-not-allowed" : "bg-white border-zinc-300 hover:border-[#1a3060]"}`}>
                      {r.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => { setOpen(false); navigate("/matches"); }} data-testid="onboarding-skip-btn">I'll do this later</Button>
          <Button onClick={save} disabled={saving || !selected.length} data-testid="onboarding-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Save roles &amp; see matches
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
